import React from "react";

import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import LoadingButton from "@mui/lab/LoadingButton";
import CircularProgress from "@mui/material/CircularProgress";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import { addEntityDocuments, documentFileUpload , sendNewDocumentsEmail} from "../OnBoarding/slice";
import { responsiveValue } from "../../theme/Styles/responsiveValue";
import { FileType, DocumentStatus } from "../../common/enum";
import { getDocuments } from "../DocumentDetails/slice";

const UploadDocument = ({ t, setIsUploadDocument }) => {
  const dispatch = useDispatch();
  const { auth: { user: { userId } = {}, profile: { entity } } = {} } =
    useSelector((state) => state);
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = React.useState(false);
  const [businessRegistration, setBusinessRegistration] = React.useState(null);
  const [mandatedStatement, setMandatedStatement] = React.useState(null);
  const [businessRegPercentage, setbusinessRegPercentage] = React.useState(0);
  const [businessRegistrationUri, setBusinessRegistrationUri] =
    React.useState(null);
  const [mandatePercentage, setMandatePercentage] = React.useState(0);
  const [mandatedStatementUri, setMandatedStatementUri] = React.useState(null);

  const businessRegistrationRef = React.useRef();

  const mandatedStatementRef = React.useRef();

  function toLocalISOString(inputDate) {
    const date = inputDate instanceof Date ? inputDate : new Date(inputDate);
  
    if (isNaN(date)) {
      throw new Error('Invalid date');
    }
  
    const offsetMs = date.getTimezoneOffset() * 60000;
    const adjustedDate = new Date(date.getTime() - offsetMs);
    return adjustedDate.toISOString().slice(0, -1);
  }

  const uploadDocumnetDetails = async (filetype, fileName) => {
    const date = new Date().setFullYear(new Date().getFullYear() + 2);
    const documentDetails = {
      loggedInUserId: userId,
      entityId: userId,
      updatedBy: userId,
      status: DocumentStatus.waitingForValidation,
      fileType: filetype,
      fileName: fileName,
      expiryDate: toLocalISOString(new Date(date)),
      uploadedDate:null,
      addnewDocument: "NEW"
    };
    await dispatch(addEntityDocuments(documentDetails));
  };

  const onUploadProgress = (progressEvent) => {
    const { loaded, total } = progressEvent;
    let percent = Math.floor((loaded * 100) / total);
    if (percent < 100) {
      setbusinessRegPercentage(percent);
    }
  };

  const onUploadProgressMandate = (progressEvent) => {
    const { loaded, total } = progressEvent;
    let percent = Math.floor((loaded * 100) / total);
    if (percent < 100) {
      setMandatePercentage(percent);
    }
  };

  const handleBusinessFileUpload = React.useCallback((e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileName = selectedFile.name;
      const fileExtension = fileName.split(".").pop().toLowerCase();
      if (fileExtension === "pdf") {
        const data = new FormData();
        data.append("file", e.target.files[0]);
        dispatch(documentFileUpload({ data, onUploadProgress }))
          .unwrap()
          .then((res) => {
            setBusinessRegistrationUri(res.fileName);
            setbusinessRegPercentage(100);
            setTimeout(() => {
              setbusinessRegPercentage(0);
            }, 1000);
            setBusinessRegistration(e.target.files?.item(0).name);
          })
          .catch((error) => {
           // console.log(error);
          });
    
        if (businessRegistrationRef.current) {
          businessRegistrationRef.current.focus();
        }
      }else{
        enqueueSnackbar({
          variant: "error",
          message: "Selecione um ficheiro tipo pdf para o registo comercial",
        });
      }
    }
    
  }, []);

  const handleMandateFileUpload = React.useCallback((e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileName = selectedFile.name;
      const fileExtension = fileName.split(".").pop().toLowerCase();
      if (fileExtension === "pdf") {
        const data = new FormData();
        data.append("file", e.target.files[0]);
        const onUploadProgress = onUploadProgressMandate;
        dispatch(documentFileUpload({ data, onUploadProgress }))
          .unwrap()
          .then((res) => {
            setMandatedStatementUri(res.fileName);
            setMandatePercentage(100);
            setTimeout(() => {
              setMandatePercentage(0);
            }, 1000);
            setMandatedStatement(e.target.files?.item(0).name);
          })
          .catch((error) => {
          });
        if (mandatedStatementRef.current) {
          mandatedStatementRef.current.focus();
        }
      }else{
        enqueueSnackbar({
          variant: "error",
          message: "Selecione um ficheiro tipo pdf para a declaração mandatada",
        });
      }
    }
    
  }, []);

  const handleSubmitDocument = React.useCallback(async () => {
    setLoading(true);
      try {
        if (businessRegistrationUri !== null && businessRegistrationUri.length > 0) {
          await uploadDocumnetDetails(
            FileType["businessRegistration"],
            businessRegistrationUri
          );
        }
        if (mandatedStatementUri !== null && mandatedStatementUri.length > 0) {
          await uploadDocumnetDetails(
            FileType["mandatedStatement"],
            mandatedStatementUri
          );
        }
        setLoading(false);
        setIsUploadDocument(false);
        enqueueSnackbar({
          message: t("documentUploadSuccessfully"),
          variant: "success",
          autoHideDuration: 3000,
        });
        
        const data = {
          entityId: entity.entityId,
          offset: 0,
          limit: 10
        };
        dispatch(getDocuments(data));
        const date = new Date().setFullYear(new Date().getFullYear() + 2);
        const documentDetailsEmail = {
            loggedInUserId: userId,
            entityId: userId,
            updatedBy: userId,
            status: DocumentStatus.waitingForValidation,
            fileType: "png",
            fileName: "new",
            expiryDate: toLocalISOString(new Date(date)),
            uploadedDate:null,
            addnewDocument: "NEW"
          };
        await dispatch(sendNewDocumentsEmail(documentDetailsEmail));

      } catch (err) {
        setLoading(false);
        enqueueSnackbar({
          message: t("someThingWentWrong"),
          variant: "error",
          autoHideDuration: 3000,
        });
        console.error(err);
      }
  }, [businessRegistrationUri,mandatedStatementUri]);

  return (
    <Paper sx={{ p: "20px" }}>
      <Typography>{t("document")}</Typography>
      <TextField
        id="outlined-basic"
        label={t("businessRegistration")}
        variant="outlined"
        inputRef={businessRegistrationRef}
        value={businessRegistration !== null ? businessRegistration : ""}
        sx={{ marginTop: "20px" }}
        helperText={businessRegistration !== null && t("fileUploaded")}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <>
              {businessRegPercentage === 0 && (
                <Button
                  variant="contained"
                  aria-label="upload"
                  component="label"
                  sx={{borderRadius:"4px"}}
                >
                   {'Selecionar'}
                  <input
                    hidden
                    accept=".pdf"
                    id="choose-file"
                    type="file"
                    onChange={handleBusinessFileUpload}
                  />
                </Button>
              )}
              {businessRegPercentage > 0 && (
                <CircularProgress
                  variant="determinate"
                  value={businessRegPercentage}
                />
              )}
            </>
          ),
        }}
        fullWidth
      />
      <TextField
        id="outlined-basic"
        label={t("mandatedStatement")}
        variant="outlined"
        inputRef={mandatedStatementRef}
        value={mandatedStatement !== null ? mandatedStatement : ""}
        sx={{ marginTop: "20px" }}
        helperText={mandatedStatement !== null && t("fileUploaded")}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <>
              {mandatePercentage === 0 && (
                <Button
                  variant="contained"
                  aria-label="upload"
                  component="label"
                  sx={{borderRadius:"4px"}}
                >
                   {'Selecionar'}
                  <input
                    hidden
                    accept=".pdf"
                    id="choose-file"
                    type="file"
                    onChange={handleMandateFileUpload}
                  />
                </Button>
              )}
              {mandatePercentage > 0 && (
                <CircularProgress
                  variant="determinate"
                  value={mandatePercentage}
                />
              )}
            </>
          ),
        }}
        fullWidth
      />
      <Box
        sx={{
          paddingX: "5px",
          display: "flex",
          justifyContent: "space-between",
          mt: 2,
        }}
      >
        <Button
          onClick={() => {
            setIsUploadDocument(false);
          }}
          sx={{
            marginTop: responsiveValue("", "", "", "0px", "0px", "10px"),
            marginLeft: responsiveValue("", "", "", "0px", "0px", "10px"),
            borderRadius:"4px"
          }}
          size="large"
          variant="contained"
          color="grey"
        >
          <Typography variant="bold">{t("toGoBack")}</Typography>
        </Button>
        <LoadingButton
          loading={loading}
          sx={{
            marginTop: responsiveValue("", "", "", "0px", "0px", "10px"),
            marginRight: responsiveValue("", "", "", "0px", "0px", "10px"),
            borderRadius:'4px'
          }}
          size="large"
          variant="contained"
          onClick={handleSubmitDocument}
          disabled={businessRegistrationUri===null && mandatedStatementUri===null}
        >
          <Typography variant="bold">{t("save")}</Typography>
        </LoadingButton>
      </Box>
    </Paper>
  );
};

export default UploadDocument;
